import { useEffect, useMemo, useRef, useState } from "react";
import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport, type UIMessage } from "ai";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { ChatInput, type ChatInputHandle } from "@/components/ChatInput";
import { ChatSidebar } from "@/components/ChatSidebar";
import {
  MCPServerPicker,
  useMCPServerCatalog,
  type MCPServer,
} from "@/components/MCPServerPicker";
import { MessageList } from "@/components/MessageList";
import { ModelPicker } from "@/components/ModelPicker";
import { detectIncompleteStop } from "@/lib/chat-status";
import { DEFAULT_MODEL_ID, type ModelId } from "@/lib/models";
import { getUserId } from "@/lib/utils";
import { CHAT_THEMES, getChatTheme, type ChatThemeId } from "@/lib/themes";

interface DbMessage {
  id: string;
  chatId: string;
  role: string;
  parts: unknown[];
  createdAt: string;
}

// Versioned: bumped to -v2 when the default-enabled set expanded from 10 to all
// 25 curated servers. A returning visitor's saved selection is keyed by version,
// so the new default set applies on next load instead of a stale narrower set.
const SELECTED_MCP_KEYS_LS = "selected_mcp_keys_v2";

function loadSelectedMcpKeys(): Set<string> {
  if (typeof window === "undefined") return new Set();
  try {
    const raw = window.localStorage.getItem(SELECTED_MCP_KEYS_LS);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    if (Array.isArray(arr)) return new Set(arr.filter((k) => typeof k === "string"));
  } catch (err) {
    console.warn("selected_mcp_keys parse failed:", err);
  }
  return new Set();
}

function saveSelectedMcpKeys(keys: Set<string>) {
  try {
    window.localStorage.setItem(SELECTED_MCP_KEYS_LS, JSON.stringify([...keys]));
  } catch (err) {
    console.warn("selected_mcp_keys save failed:", err);
  }
}

export function ChatHome() {
  const params = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [model, setModel] = useState<ModelId>(DEFAULT_MODEL_ID);
  const chatIdRef = useRef<string | undefined>(params.id);
  const [, forceRerender] = useState(0);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const userId = getUserId();
  const inputRef = useRef<ChatInputHandle>(null);

  // MCP catalog + selected
  const { servers: mcpServers, loading: mcpLoading } = useMCPServerCatalog();
  const [selectedMcpKeys, setSelectedMcpKeysState] = useState<Set<string>>(
    () => loadSelectedMcpKeys(),
  );
  const [hasInitDefaults, setHasInitDefaults] = useState(false);
  // Which portal preset is active (null = essentials/custom). Drives the
  // header switcher highlight + the themed empty-state prompts.
  const [activeTheme, setActiveTheme] = useState<ChatThemeId | null>(null);

  // Apply default-enabled keys once after the catalog loads (only if user has nothing saved).
  useEffect(() => {
    if (hasInitDefaults || mcpLoading || mcpServers.length === 0) return;
    setHasInitDefaults(true);
    // A ?theme deep-link (the Landing portal cards) wins over any saved
    // selection: enable that portal's full server roster.
    const themed = getChatTheme(searchParams.get("theme"));
    if (themed) {
      const keys = new Set(
        mcpServers.filter((s) => s.theme === themed.id).map((s) => s.key),
      );
      setSelectedMcpKeysState(keys);
      saveSelectedMcpKeys(keys);
      setActiveTheme(themed.id);
      return;
    }
    const persisted = loadSelectedMcpKeys();
    if (persisted.size > 0) {
      // Drop any keys that no longer exist server-side.
      const valid = new Set([...persisted].filter((k) => mcpServers.some((s) => s.key === k)));
      setSelectedMcpKeysState(valid);
      saveSelectedMcpKeys(valid);
      return;
    }
    const defaults = new Set(
      mcpServers.filter((s) => s.defaultEnabled).map((s) => s.key),
    );
    setSelectedMcpKeysState(defaults);
    saveSelectedMcpKeys(defaults);
  }, [mcpLoading, mcpServers, hasInitDefaults, searchParams]);

  const setSelectedMcpKeys = (keys: Set<string>) => {
    setSelectedMcpKeysState(keys);
    saveSelectedMcpKeys(keys);
  };

  // Apply a portal preset: enable exactly that theme's servers + reflect it in
  // the URL so the choice is shareable.
  const selectTheme = (id: ChatThemeId) => {
    const keys = new Set(
      mcpServers.filter((s) => s.theme === id).map((s) => s.key),
    );
    setSelectedMcpKeys(keys);
    setActiveTheme(id);
    const next = new URLSearchParams(searchParams);
    next.set("theme", id);
    setSearchParams(next, { replace: true });
  };

  // Refs for values the transport's `body()` closure reads at request time.
  // useChat keeps the original transport instance, so we can't rely on a fresh
  // closure per render — instead the body() callback dereferences these refs.
  const mcpServersCatalogRef = useRef<MCPServer[]>([]);
  const selectedMcpKeysRef = useRef<Set<string>>(selectedMcpKeys);
  const modelRef = useRef<ModelId>(model);

  useEffect(() => {
    mcpServersCatalogRef.current = mcpServers;
  }, [mcpServers]);
  useEffect(() => {
    selectedMcpKeysRef.current = selectedMcpKeys;
  }, [selectedMcpKeys]);
  useEffect(() => {
    modelRef.current = model;
  }, [model]);

  // Keep chatId ref in sync with URL changes (sidebar nav, deep links).
  useEffect(() => {
    chatIdRef.current = params.id;
    forceRerender((n) => n + 1);
  }, [params.id]);

  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: "/api/chat",
        headers: () => ({
          "x-user-id": userId,
          "x-model-id": modelRef.current,
        }),
        body: () => {
          const selected = selectedMcpKeysRef.current;
          const catalog = mcpServersCatalogRef.current;
          const mcpServersForReq = catalog
            .filter((s) => selected.has(s.key))
            .map((s: MCPServer) => ({
              name: s.key,
              url: s.url,
              type: s.type,
            }));
          return {
            chatId: chatIdRef.current,
            selectedModel: modelRef.current,
            userId,
            mcpServers: mcpServersForReq,
          };
        },
      }),
    [userId],
  );

  const [sidebarRefreshKey, setSidebarRefreshKey] = useState(0);

  const { messages, sendMessage, status, error, setMessages, stop } = useChat({
    transport,
    onFinish: () => {
      // Refresh the sidebar so the newly-created (or just-titled) chat appears.
      setSidebarRefreshKey((k) => k + 1);
    },
  });

  // Load existing messages when navigating to /chat/:id
  useEffect(() => {
    if (!params.id) {
      setMessages([]);
      return;
    }
    let cancelled = false;
    setLoadingHistory(true);
    fetch(`/api/chats/${params.id}`, { headers: { "x-user-id": userId } })
      .then((r) => r.json() as Promise<{ messages?: DbMessage[] }>)
      .then((j) => {
        if (cancelled) return;
        const msgs: UIMessage[] = Array.isArray(j.messages)
          ? j.messages.map((m) => ({
              id: m.id,
              role: m.role as UIMessage["role"],
              parts: Array.isArray(m.parts)
                ? (m.parts as UIMessage["parts"])
                : [{ type: "text", text: "" }],
            }))
          : [];
        setMessages(msgs);
      })
      .catch((err) => console.error("history load failed:", err))
      .finally(() => {
        if (!cancelled) setLoadingHistory(false);
      });
    return () => {
      cancelled = true;
    };
  }, [params.id, userId, setMessages]);

  // Only treat actively in-flight states as "streaming" — `error` and `ready`
  // both mean the input should be re-enabled so the user can recover by
  // sending another message (or retrying).
  const isStreaming = status === "submitted" || status === "streaming";

  // Detect the "model wanted more tools but the server-side step cap closed
  // the loop" case (chat.ts:62 MAX_STEPS). Manifests as `finishReason:"tool-calls"`
  // on the wire — clean from `useChat`'s POV, so `error` stays null. Without
  // this banner the user sees the chat just stop after N tool calls.
  const incompleteStop = detectIncompleteStop(messages, status, !!error);

  const handleSend = (text: string) => {
    if (!chatIdRef.current) {
      const newId = `chat_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
      chatIdRef.current = newId;
      window.history.replaceState(null, "", `/chat/${newId}`);
    }
    sendMessage({ text });
  };

  const handleNewChat = () => {
    chatIdRef.current = undefined;
    setMessages([]);
    navigate("/chat", { replace: false });
  };

  useEffect(() => {
    const incoming = searchParams.get("prompt");
    if (!incoming) return;
    inputRef.current?.setValue(incoming);
    const next = new URLSearchParams(searchParams);
    next.delete("prompt");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  return (
    <div className="flex h-screen overflow-hidden">
      <ChatSidebar
        onNewChat={handleNewChat}
        refreshSignal={sidebarRefreshKey}
        activeStreamingChatId={isStreaming ? chatIdRef.current : undefined}
      />
      <div className="flex flex-1 flex-col">
        <header className="relative flex items-center justify-between border-b border-border px-4 py-2 gap-3">
          <div className="flex items-center gap-2">
            <h1 className="text-base font-semibold">Bio MCP Portal</h1>
            <span
              className="text-xs text-muted-foreground"
              data-testid="runtime-badge"
            >
              cloudflare-workers
            </span>
            {isStreaming && (
              <span
                data-testid="header-streaming-badge"
                className="ml-2 inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[11px] font-medium text-emerald-600 dark:text-emerald-400"
              >
                <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />
                {status === "submitted" ? "Thinking…" : "Streaming…"}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <div
              className="hidden items-center rounded-md border border-input bg-background p-0.5 text-xs md:flex"
              data-testid="theme-switcher"
              title="Mirror a portal: enable that domain's full server set"
            >
              {CHAT_THEMES.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => selectTheme(t.id)}
                  title={t.blurb}
                  data-testid={`theme-${t.id}`}
                  className={`rounded px-2 py-1 transition-colors ${
                    activeTheme === t.id
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:bg-accent"
                  }`}
                >
                  {t.short}
                </button>
              ))}
            </div>
            <MCPServerPicker
              servers={mcpServers}
              selectedKeys={selectedMcpKeys}
              onChange={(keys) => {
                setSelectedMcpKeys(keys);
                setActiveTheme(null);
              }}
            />
            <ModelPicker value={model} onChange={setModel} disabled={isStreaming} />
          </div>
          {isStreaming && (
            <div
              className="shimmer-bar pointer-events-none absolute inset-x-0 -bottom-px h-px overflow-hidden"
              data-testid="header-shimmer"
              aria-hidden="true"
            />
          )}
        </header>
        {loadingHistory && (
          <div className="border-b border-border bg-muted/30 px-4 py-2 text-center text-xs text-muted-foreground">
            Loading conversation…
          </div>
        )}
        <MessageList
          messages={messages}
          isStreaming={isStreaming}
          examplePrompts={
            activeTheme ? getChatTheme(activeTheme)?.prompts : undefined
          }
          onExamplePrompt={(text) => inputRef.current?.setValue(text)}
        />
        {error && (
          <div
            data-testid="chat-error"
            className="border-t border-destructive bg-destructive/10 p-3 text-sm text-destructive"
          >
            <div className="mx-auto max-w-3xl space-y-1">
              <div className="font-semibold">Stream interrupted</div>
              <div className="text-xs opacity-90">{error.message}</div>
              {/^.*rate limit.*input tokens.*$/i.test(error.message) && (
                <div className="text-xs opacity-90">
                  💡 Tool results above are preserved. Try the question again
                  with <code className="rounded bg-destructive/15 px-1">codemode_sandbox</code>{" "}
                  to filter or aggregate before the data is sent back to the model,
                  or pick a less-rate-limited model from the picker.
                </div>
              )}
            </div>
          </div>
        )}
        {incompleteStop === "step-cap" && (
          <div
            data-testid="chat-incomplete-stop"
            className="border-t border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-700 dark:text-amber-300"
          >
            <div className="mx-auto max-w-3xl space-y-1">
              <div className="font-semibold">Stopped before final answer</div>
              <div className="text-xs opacity-90">
                The model wanted to keep calling tools but the per-turn step
                cap fired (server-side <code className="rounded bg-amber-500/15 px-1">MAX_STEPS</code>).
                Tool results above are preserved — ask a follow-up like
                &quot;synthesize what you have so far&quot; to get the final answer
                without re-running the queries.
              </div>
            </div>
          </div>
        )}
        {isStreaming && (
          <div className="border-t border-border bg-muted/30 px-3 py-1 text-center">
            <button
              type="button"
              data-testid="chat-stop"
              onClick={() => stop()}
              className="text-xs text-muted-foreground underline-offset-2 hover:underline"
            >
              Stop generating
            </button>
          </div>
        )}
        <ChatInput ref={inputRef} onSend={handleSend} disabled={isStreaming} />
      </div>
    </div>
  );
}
